// Telehealth (provider side) — appointments, messaging, live visit.
// All under /api/edi/televisit/appointments (proxied). See the approved Telehealth plan.
import { api } from "@/lib/api";

const B = "/api/edi/televisit/appointments";

export async function listAppointments(clientId, { etag } = {}) {
  const { data } = await api.get(B, { params: { client_id: clientId, limit: 200 }, ...(etag ? { etag: true } : {}) });
  return data?.appointments || [];
}
export async function getAppointment(id) { const { data } = await api.get(`${B}/${id}`); return data?.appointment || data; }
export async function startVisit(id) { const { data } = await api.post(`${B}/${id}/start`, {}); return data?.appointment || data; }
export async function completeVisit(id) { const { data } = await api.post(`${B}/${id}/complete`, {}); return data?.appointment || data; }
export async function updateAppointment(id, body) { const { data } = await api.patch(`${B}/${id}`, body); return data?.appointment || data; }
export async function deleteAppointment(id) { const { data } = await api.delete(`${B}/${id}`); return data; }
export async function listMessages(id) { const { data } = await api.get(`${B}/${id}/messages`); return data?.messages || []; }
export async function sendMessage(id, text) { const { data } = await api.post(`${B}/${id}/message`, { text }); return data?.messages || []; }
// Care checklist (engagement events) — served by its own endpoint, not on the appointment.
export async function getEngagementEvents(id) { const { data } = await api.get(`${B}/${id}/engagement-events`); return { available: data?.available !== false, events: data?.events || [] }; }
export async function setEngagementStatus(id, eventId, status) { const { data } = await api.patch(`${B}/${id}/engagement-events/${eventId}`, { status }); return data?.events || []; }
// No server-side regenerate (events are deterministic from the chart) — just re-fetch.
export async function regenerateEngagement(id) { const { data } = await api.get(`${B}/${id}/engagement-events`); return data?.events || []; }
export async function markRead(id) { try { return await updateAppointment(id, { provider_last_read_at: new Date().toISOString() }); } catch (e) { return null; } }

// --- Post-visit pipeline (Telemed Activity Tracker) ---
// Visit-level endpoints live at /api/edi/televisit/{visit_id} (NOT /appointments).
// The upstream runs fetch->transcribe->insights->SOAP->create-claim server-side;
// process is async ({queued:true}) so we poll getVisit for progress.
const V = "/api/edi/televisit";
export async function getVisit(visitId) { const { data } = await api.get(`${V}/${visitId}`); return data?.visit || data; }
export async function generateVisitSoap(visitId) { const { data } = await api.post(`${V}/${visitId}/soap`, {}); return data?.visit || data; }
// One /soap per visit at a time across the app (call screen + Activity auto-run share this).
const _soapInFlight = new Map();
export function requestSoap(visitId) {
  if (!_soapInFlight.has(visitId)) _soapInFlight.set(visitId, generateVisitSoap(visitId).finally(() => _soapInFlight.delete(visitId)));
  return _soapInFlight.get(visitId);
}
export const hasTranscript = (v) => !!v && (v.status === "transcribed" || _has(v.transcript) || _has(v.transcript_meta));
export const hasSoap = (v) => !!v && (_has(v.soap_text) || _has(v.soap_note));
// After Complete our server runs the note pipeline (60 s wait → /process → /soap) on its own,
// so a reload or closed tab can't stop it. The screen only reads its status.
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const PIPE = `${process.env.REACT_APP_BACKEND_URL}/api/visit-pipeline/jobs`;
export const PIPE_ACTIVE = ["waiting", "fetching", "writing"];
export async function getPipelineJobs() { const { data } = await api.get(PIPE); return data?.jobs || []; }
export async function getPipelineJob(visitId) {
  try { const { data } = await api.get(`${PIPE}/${visitId}`); return data; } catch (e) { if (e?.response?.status === 404) return null; throw e; }
}
export async function retryVisitNote(visitId) { const { data } = await api.post(`${PIPE}/${visitId}/retry`, {}); return data; }
export const jobSecs = (j) => (j?.state === "waiting" ? Math.max(0, Math.ceil(j.due_at - Date.now() / 1000)) : 0);
export async function watchVisitNote(visitId, { onWait = () => {}, timeoutMs = 1260000 } = {}) {
  const t0 = Date.now(); let missing = 0;
  while (Date.now() - t0 < timeoutMs) {
    let j = null; try { j = await getPipelineJob(visitId); } catch { /* keep polling */ }
    if (j?.state === "done") { try { return await getVisit(visitId); } catch { return null; } }
    if (j?.state === "error" || j?.state === "skipped") throw new Error(j.message || "The recording could not be processed.");
    if (j?.state === "waiting") { onWait({ phase: "waiting", secs: jobSecs(j) }); await sleep(1000); continue; }
    if (j) onWait({ phase: j.state === "writing" ? "soap" : "fetching", message: j.message });
    else if (++missing > 5) throw new Error("The note pipeline didn't start for this visit.");
    await sleep(3000);
  }
  throw new Error("Still waiting on the recording. It keeps running on the server — check Activity.");
}
export async function transcribeVisit(visitId) { const { data } = await api.post(`${V}/${visitId}/process`, {}); return data?.visit || data; }
export async function sendVisitToClaim(visitId) { const { data } = await api.post(`${V}/${visitId}/send-to-claim`, {}); return data?.visit || data; }

// §6.10 Session state — freeze/unfreeze a visit so NO fetch/transcribe/pipeline ever runs
// (manual, bulk, or Daily webhook) until marked active again. stale/removed are identical
// freezes with different labels; active unfreezes. Stored as visit.session_state.
export const SESSION_STATES = {
  active: { label: "Active", color: "#166534", bg: "#E7F7EC" },
  stale: { label: "Stale", color: "#9A5A00", bg: "#FFF4E0" },
  removed: { label: "Removed", color: "#B42318", bg: "#FCE9E7" },
};
export async function setSessionState(visitId, state) { const { data } = await api.post(`${V}/${visitId}/state`, { state }); return data?.visit || data; }
export const isFrozen = (v) => v?.session_state === "stale" || v?.session_state === "removed";

// Early end: same backend close as Complete (session_ended → /complete), then freeze as stale so no note is made.
export const ENDED_EARLY_LABEL = "Provider ended the session early";
export const isEndedEarly = (v) => (v?.session_events || []).some((e) => e?.meta?.ended_early || e?.label === ENDED_EARLY_LABEL);
export async function terminateVisit(apptId, visitId) {
  if (visitId) await api.post(`${V}/${visitId}/event`, { event: "session_ended", label: ENDED_EARLY_LABEL, meta: { ended_early: true } }).catch(() => {});
  await completeVisit(apptId);
  if (visitId) await setSessionState(visitId, "stale");
}

export const PIPELINE_STAGES = [
  { key: "recording", label: "Recording" },
  { key: "transcript", label: "Transcript" },
  { key: "insights", label: "Insights" },
  { key: "soap", label: "SOAP note" },
  { key: "claim", label: "Claim" },
];
const _has = (x) => x != null && x !== "" && !(Array.isArray(x) && x.length === 0) && !(typeof x === "object" && !Array.isArray(x) && Object.keys(x || {}).length === 0);
// A recording_error can be transient ("not ready yet — try again") while Daily
// finishes processing, or permanent (no/empty/silent recording). Either way the
// upstream pipeline reports stage:"error" and the visit must NOT be auto-retried
// in a loop — it belongs in the "Failed recordings" list for on-demand retry.
const PERMANENT_REC_ERR = /(no recording|not found|empty|silent|no audio|no media|failed|unavailable|too short|expired|deleted|corrupt)/i;
const STAGE_TO_STEP = {
  queued: "recording", fetching_recording: "recording", downloading: "recording", recording: "recording",
  transcribing: "transcript", transcript: "transcript",
  insights: "insights",
  soap: "soap",
  claim: "claim", complete: "claim", done: "claim",
};

// Derive pipeline UI state. Prefers the authoritative visit.pipeline object
// ({stage,label,pct,message}) and falls back to field presence.
export function pipelineState(v) {
  if (!v) return { done: {}, active: null, step: null, label: "", pct: 0, message: "", error: null, errorKind: null, complete: false, failed: false, needsManual: false, claim_status: null, note_id: null };
  const hasTx = _has(v.transcript) || _has(v.transcript_meta) || v.status === "transcribed";
  const done = {
    recording: !!v.recording_id || hasTx,
    transcript: hasTx,
    insights: _has(v.insights),
    soap: _has(v.soap_text) || _has(v.soap_note),
    claim: !!v.note_id,
  };
  const p = v.pipeline || null;
  const error = v.recording_error || (p && p.stage === "error" ? p.message : null);
  const errorKind = error ? (PERMANENT_REC_ERR.test(error) ? "permanent" : "transient") : null;
  // "done" after a transcribe-only run is NOT a finished note — complete needs a SOAP note or a saved encounter.
  const complete = !!v.note_id || done.soap;
  // Guide §4: only pipeline.stage === "error" is terminal; "processing / fetching_recording" is the API retrying.
  const failed = !complete && !!(p && p.stage === "error");
  const needsManual = v.needs_manual === true || failed;
  let active = null;
  if (!complete && !failed) {
    active = p && p.stage && p.stage !== "done" && p.stage !== "complete" ? (STAGE_TO_STEP[p.stage] || "recording") : (PIPELINE_STAGES.find((s) => !done[s.key])?.key || null);
  }
  return {
    done, active, step: active,
    label: p?.label || "", pct: typeof p?.pct === "number" ? p.pct : null, message: p?.message || error || "",
    error, errorKind, complete, failed, needsManual,
    claim_status: v.claim_status, note_id: v.note_id,
  };
}

// --- documents, patient-medical, pipeline queue, bulk processing ---
export async function getAppointmentDocuments(apptId) {
  try { const { data } = await api.get(`/api/edi/televisit/appointments/${apptId}/documents`); return data?.documents || (Array.isArray(data) ? data : []); }
  catch (e) { if (e?.response?.status === 404) return []; throw e; }
}
export async function getAppointmentDocument(apptId, docId) {
  const { data } = await api.get(`/api/edi/televisit/appointments/${apptId}/document/${docId}`);
  return data?.document || data;
}
export async function getPipelineQueue(clientId) {
  const { data } = await api.get(`/api/edi/televisit/pipeline-queue`, { params: { client_id: String(clientId) } });
  return data || { active: [], recent: [] };
}
export async function processBatch(body) {
  const { data } = await api.post(`/api/edi/televisit/process-batch`, body);
  return data;
}

// Deterministic EHI Bridge chart snapshot (no AI/credits). Returns rich, dated
// structures for the live-visit side panel (problems split chronic/acute, meds with
// start/end dates, abnormal labs with trend, vitals, allergies) plus a flat `meds`
// string list the med-staging tab consumes.
const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));
export async function getChartSnapshot(chartUuid) {
  if (!chartUuid) return null;
  const { data } = await api.get(`/api/ehibridge/chart/${encodeURIComponent(chartUuid)}/summary`);
  const s = data?.sections || {};
  const med = s.medications || {}, dx = s.diagnoses || {}, al = s.allergies || {}, vit = s.vitals || {}, labs = s.labs || {};
  const meds = uniq([...(med.active || []), ...(med.expiring_soon || [])]
    .map((m) => `${m.medication_name || ""}${m.dosage ? ` ${m.dosage}` : ""}`.trim()));
  return {
    headline: data?.headline,
    patient_name: data?.patient_name,
    meds, // flat strings for MedStaging
    medications: {
      active: med.active || [],
      expiring_soon: med.expiring_soon || [],
      counts: med.counts || {},
    },
    problems: {
      chronic: dx.active_chronic || [],
      acute: dx.active_other || [],
      counts: dx.counts || {},
    },
    labs: {
      abnormal: labs.abnormal_recent || [],
      window_days: labs.window_days || 90,
      counts: labs.counts || {},
    },
    vitals: {
      recorded_at: vit.recorded_at || null,
      items: vit.items || [],
    },
    allergies: {
      nkda: !!al.nkda,
      items: al.items || [],
    },
  };
}

// Pre-visit intake — the patient's auto-generated questions + submitted answers,
// read via the patient's join token. Read-only on the provider side.
export async function getIntake(token) {
  const { data } = await api.get(`/api/edi/televisit/join/${token}/intake`);
  return data || { questions: [], answers: [], completed: false };
}

// Live join status for a visit — consent, whether the patient has joined, and
// unread message count — read via the patient's join token (read-only).
export async function getJoinStatus(token) {
  const { data } = await api.get(`/api/edi/televisit/join/${token}`);
  return data || {};
}

// Answers come back either as an array ([{question_id|id, answer|value|response|text}])
// or an object keyed by question id. Normalise to a {questionId: answerString} map.
export function mapIntakeAnswers(answers) {
  const out = {};
  if (!answers) return out;
  if (Array.isArray(answers)) {
    for (const a of answers) {
      const id = a.question_id ?? a.qid ?? a.id ?? a.key;
      const v = a.answer ?? a.value ?? a.response ?? a.text ?? "";
      if (id != null) out[String(id)] = typeof v === "boolean" ? (v ? "Yes" : "No") : String(v ?? "");
    }
  } else if (typeof answers === "object") {
    for (const [k, v] of Object.entries(answers)) out[String(k)] = typeof v === "boolean" ? (v ? "Yes" : "No") : String(v ?? "");
  }
  return out;
}

// Row-level intake state from whatever the appointment carries (no extra call).
export function intakeStateOf(a) {
  if (a.intake_completed === true || a.intake_status === "completed") return "done";
  if (a.intake_completed === false || a.intake_status === "pending" || a.intake_status === "sent") return "pending";
  return null; // unknown — the appointment list didn't carry it
}

// The "ended" statuses share one definition everywhere.
export const ENDED_STATUSES = ["completed", "cancelled", "canceled", "no_show"];
export const isEnded = (s) => ENDED_STATUSES.includes(s);
export const isStarted = (s) => s === "in_progress" || s === "waiting_room" || s === "waiting";

export const APPT_STATUS = {
  scheduled: { t: "Scheduled", bg: "#EEF0FF", fg: "#3730A3" },
  confirmed: { t: "Confirmed", bg: "#E7F7EC", fg: "#166534" },
  waiting_room: { t: "Waiting room", bg: "#FFF4E0", fg: "#9A5A00" },
  waiting: { t: "Waiting room", bg: "#FFF4E0", fg: "#9A5A00" },
  in_progress: { t: "In progress", bg: "#E4F0FF", fg: "#1E40AF" },
  completed: { t: "Completed", bg: "#EDEDEF", fg: "#44443F" },
  cancelled: { t: "Cancelled", bg: "#FCE9E7", fg: "#B42318" },
  canceled: { t: "Cancelled", bg: "#FCE9E7", fg: "#B42318" },
  no_show: { t: "No-show", bg: "#FCE9E7", fg: "#B42318" },
};
export const statusMeta = (s) => APPT_STATUS[s] || { t: s || "—", bg: "#EDEDEF", fg: "#44443F" };

export const ENGAGEMENT_CAT = {
  vaccination: { t: "Vaccination", c: "#166534" },
  screening: { t: "Screening", c: "#1E40AF" },
  preventive: { t: "Preventive", c: "#9A5A00" },
  monitoring: { t: "Monitoring", c: "#5B21B6" },
  chronic_care: { t: "Chronic care", c: "#7C3AED" },
  medication: { t: "Medication", c: "#B45309" },
  lifestyle: { t: "Lifestyle", c: "#0E7490" },
};
export const catMeta = (c) => ENGAGEMENT_CAT[c] || { t: c || "Care", c: "#6C6C69" };

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
// An appointment belongs to the open chart if it links by uuid / mrn / chart_id, or matches
// the patient's name (manual bookings often have null linkage).
export function matchesChart(a, ctx) {
  const p = a.patient || {};
  if (ctx.uuid && (p.chart_uuid === ctx.uuid)) return true;
  if (ctx.mrn && p.mrn && norm(p.mrn) === norm(ctx.mrn)) return true;
  if (ctx.chartId && p.chart_id && norm(p.chart_id) === norm(ctx.chartId)) return true;
  if (ctx.name && p.name && norm(p.name) === norm(ctx.name)) return true;
  return false;
}
